import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import { pFetch, authQuery, authHeaders } from "../portal/portalAuth";
import "./ToolsKomprimierungPage.css";

const fmt = (b) => {
  if (b == null) return "";
  if (b > 1e9) return (b / 1e9).toFixed(2) + " GB";
  if (b > 1e6) return (b / 1e6).toFixed(2) + " MB";
  if (b > 1e3) return (b / 1e3).toFixed(1) + " KB";
  return b + " B";
};

/* Welle 84: HTML-Fehlerseiten (nginx 413/502/504) sauber melden statt "Unexpected token <" */
async function readJsonOrThrow(r) {
  const text = await r.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }
  if (!r.ok) {
    const kurz = (data && data.detail) ? String(data.detail) : text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160);
    const hinweis = r.status === 413 ? " — Datei zu groß für den Server (nginx client_max_body_size)"
      : r.status === 504 ? " — Zeitüberschreitung am Proxy" : r.status === 502 ? " — Backend nicht erreichbar" : "";
    throw new Error(`HTTP ${r.status} ${r.statusText}${hinweis}${kurz ? ": " + kurz : ""}`);
  }
  if (data == null) throw new Error("Antwort war kein JSON: " + text.slice(0, 120));
  return data;
}

/* Upload mit Fortschritt (fetch kann keinen Upload-Progress) */
function xhrUpload(url, formData, { onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    for (const [k, v] of Object.entries(authHeaders())) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round(e.loaded / e.total * 100)); };
    xhr.onload = () => {
      const fake = { ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, statusText: xhr.statusText,
        text: async () => xhr.responseText };
      readJsonOrThrow(fake).then(resolve, reject);
    };
    xhr.onerror = () => reject(new Error("Netzwerkfehler beim Upload"));
    xhr.onabort = () => reject(new Error("Upload abgebrochen"));
    if (signal) signal.addEventListener("abort", () => xhr.abort());
    xhr.send(formData);
  });
}

const HASH_MAX_BYTES = 50 * 1024 * 1024;   // Welle 84: Dropbox-Hash nur bis 50 MB im Browser rechnen

const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".heic", ".heif", ".webp", ".bmp", ".tiff", ".tif", ".gif"];
const VIDEO_EXTS = [".mp4", ".mov", ".mkv", ".avi", ".webm", ".m4v"];
const PDF_EXTS = [".pdf"];

/**
 * portal: true = externes Tools-Portal (kein Home-Button, kein Dropbox/Synology)
 * allowed: {pdf, bild, video} — welche Dateitypen angenommen werden (null = alle)
 */
export default function ToolsKomprimierungPage({ portal = false, allowed = null }) {
  const navigate = useNavigate();
  const allowedExts = allowed
    ? [
        ...(allowed.pdf ? PDF_EXTS : []),
        ...(allowed.bild ? IMAGE_EXTS : []),
        ...(allowed.video ? VIDEO_EXTS : []),
      ]
    : [...IMAGE_EXTS, ...VIDEO_EXTS, ...PDF_EXTS];
  const [files, setFiles] = useState([]); // {file, status, result?, error?, dropboxPath?}
  const [busy, setBusy] = useState(false);
  const [synBusy, setSynBusy] = useState(false);
  const [health, setHealth] = useState(null);
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    pFetch(`${API_BASE}/tools/komprimieren/health`).then(r => r.json()).then(setHealth).catch(() => {});
  }, []);

  // Berechnet den Dropbox Content-Hash (SHA256 über 4MB-Blöcke)
  const computeDropboxHash = async (file) => {
    const BLOCK = 4 * 1024 * 1024;
    const buf = await file.arrayBuffer();
    const n = Math.max(1, Math.ceil(buf.byteLength / BLOCK));
    const blockHashes = [];
    for (let i = 0; i < n; i++) {
      const block = buf.slice(i * BLOCK, (i + 1) * BLOCK);
      blockHashes.push(await crypto.subtle.digest("SHA-256", block));
    }
    const combined = new Uint8Array(blockHashes.reduce((s, h) => s + h.byteLength, 0));
    let off = 0;
    for (const h of blockHashes) { combined.set(new Uint8Array(h), off); off += h.byteLength; }
    const final = await crypto.subtle.digest("SHA-256", combined.buffer);
    return [...new Uint8Array(final)].map(b => b.toString(16).padStart(2, "0")).join("");
  };

  // Sucht eine Datei per Name + Content-Hash in Dropbox und füllt den Pfad auto
  const detectDropboxPath = async (file, fileIdx) => {
    try {
      const hash = file.size <= HASH_MAX_BYTES ? await computeDropboxHash(file) : "";
      const fd = new FormData();
      fd.append("filename", file.name);
      fd.append("content_hash", hash);
      const r = await pFetch(`${API_BASE}/tools/komprimieren/dropbox-find`, { method: "POST", body: fd });
      const data = await readJsonOrThrow(r);
      if (data.found && data.path) {
        setFiles(prev => prev.map((f, i) =>
          i === fileIdx ? { ...f, dropboxPath: data.path, syn: false } : f
        ));
      }
    } catch {
      // Lookup-Fehler ignorieren
    }
  };

  const addFiles = useCallback((list) => {
    let arr = Array.from(list || []);
    // Nur freigegebene Dateitypen annehmen
    arr = arr.filter(f => allowedExts.some(ext => f.name.toLowerCase().endsWith(ext)));
    if (!arr.length) return;
    setFiles(prev => {
      const startIdx = prev.length;
      const newEntries = arr.map(f => ({
        file: f, status: "pending", result: null, error: null, syn: !portal, dropboxPath: "",
      }));
      // Dropbox-Hash-Lookup nur intern (im Portal weder nötig noch erlaubt)
      if (!portal) {
        setTimeout(() => arr.forEach((f, i) => detectDropboxPath(f, startIdx + i)), 0);
      }
      return [...prev, ...newEntries];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portal]);


  // Drag&Drop
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onOver = (e) => { e.preventDefault(); el.classList.add("dragover"); };
    const onLeave = () => el.classList.remove("dragover");
    const onDrop = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
      addFiles(e.dataTransfer.files);
    };
    el.addEventListener("dragover", onOver);
    el.addEventListener("dragleave", onLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onOver);
      el.removeEventListener("dragleave", onLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [addFiles]);

  const abortRef = useRef(null);
  const [uploadPct, setUploadPct] = useState(null);

  /* Welle 84: Upload mit Fortschritt, danach Polling der Jobs (Komprimierung läuft im Hintergrund) */
  const pollJobs = async (jobIds) => {
    let offen = new Set(jobIds);
    while (offen.size > 0) {
      await new Promise((res) => setTimeout(res, 2000));
      let data;
      try {
        const r = await pFetch(`${API_BASE}/tools/komprimieren/status?ids=${encodeURIComponent([...offen].join(","))}`);
        data = await readJsonOrThrow(r);
      } catch (e) {
        setFiles(prev => prev.map(f => (f.jobId && offen.has(f.jobId)) ? { ...f, error: String(e.message || e) } : f));
        continue;
      }
      const byId = Object.fromEntries((data.results || []).map(x => [x.job_id, x]));
      setFiles(prev => prev.map(f => {
        const res = f.jobId && byId[f.jobId];
        if (!res) return f;
        if (res.status === "ok") { offen.delete(f.jobId); return { ...f, status: "done", result: res, error: null, progress: 100 }; }
        if (res.status === "error") { offen.delete(f.jobId); return { ...f, status: "error", result: res, error: res.error || "Fehler", progress: null }; }
        return { ...f, status: "processing", progress: res.progress_pct, stage: res.stage, result: { ...res, download_id: null } };
      }));
    }
  };

  const startCompression = async () => {
    const pending = files.filter(f => f.status === "pending");
    if (!pending.length) return;
    setBusy(true);
    setFiles(prev => prev.map(f => f.status === "pending" ? { ...f, status: "uploading", error: null, progress: 0 } : f));
    const fd = new FormData();
    pending.forEach(f => fd.append("files", f.file));
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setUploadPct(0);
    try {
      const data = await xhrUpload(`${API_BASE}/tools/komprimieren/upload`, fd, {
        onProgress: (p) => setUploadPct(p), signal: ctrl.signal,
      });
      const ids = [];
      setFiles(prev => {
        const copy = [...prev];
        let pi = 0;
        for (let i = 0; i < copy.length; i++) {
          if (copy[i].status === "uploading" && data.results[pi]) {
            const res = data.results[pi];
            if (res.status === "error") {
              copy[i] = { ...copy[i], status: "error", result: res, error: res.error || "Fehler" };
            } else {
              ids.push(res.job_id);
              copy[i] = { ...copy[i], status: "processing", jobId: res.job_id, progress: 0, result: res };
            }
            pi++;
          }
        }
        return copy;
      });
      setUploadPct(null);
      if (ids.length) await pollJobs(ids);
    } catch (e) {
      setFiles(prev => prev.map(f => (f.status === "uploading" || f.status === "processing") ? { ...f, status: "error", error: String(e.message || e) } : f));
    } finally {
      setUploadPct(null);
      abortRef.current = null;
      setBusy(false);
    }
  };

  const downloadFile = (item) => {
    if (!item.result?.download_id) return;
    const a = document.createElement("a");
    a.href = `${API_BASE}/tools/komprimieren/download/${item.result.download_id}?${authQuery()}`;
    a.download = item.result.download_name || item.file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadAll = () => {
    files.filter(f => f.status === "done").forEach(downloadFile);
  };

  const uploadToSynology = async () => {
    const ids = files
      .filter(f => f.syn && f.result?.orig_id && f.status === "done")
      .map(f => f.result.orig_id);
    if (!ids.length) {
      alert("Keine Dateien für Synology-Upload markiert");
      return;
    }
    setSynBusy(true);
    try {
      const fd = new FormData();
      fd.append("orig_ids", ids.join(","));
      const r = await pFetch(`${API_BASE}/tools/komprimieren/synology-upload`, { method: "POST", body: fd });
      const data = await readJsonOrThrow(r);
      const ok = (data.results || []).filter(x => x.status === "ok").length;
      const err = (data.results || []).filter(x => x.status === "error").length;
      alert(`Synology-Upload abgeschlossen.\nErfolg: ${ok}\nFehler: ${err}\nZielordner: ${data.target_dir}`);
    } catch (e) {
      alert("Fehler: " + (e.message || e));
    } finally {
      setSynBusy(false);
    }
  };

  const removeItem = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const replaceInDropbox = async (item, idx) => {
    if (!item.result?.download_id || !item.dropboxPath) return;
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, dbxBusy: true, dbxError: null } : f));
    try {
      const fd = new FormData();
      fd.append("job_id", item.result.download_id);
      fd.append("dropbox_path", item.dropboxPath);
      const r = await pFetch(`${API_BASE}/tools/komprimieren/dropbox-replace`, { method: "POST", body: fd });
      const data = await readJsonOrThrow(r);
      setFiles(prev => prev.map((f, i) => i === idx ? { ...f, dbxBusy: false, dbxDone: true, dbxPath: data.dropbox_path } : f));
    } catch (e) {
      setFiles(prev => prev.map((f, i) => i === idx ? { ...f, dbxBusy: false, dbxError: String(e) } : f));
    }
  };


  const clearAll = () => setFiles([]);

  const toggleSyn = (idx) => setFiles(prev => prev.map((f, i) => i === idx ? { ...f, syn: !f.syn } : f));

  const totalOrig = files.reduce((a, f) => a + (f.result?.original_size || f.file.size), 0);
  const totalKomp = files.reduce((a, f) => a + (f.result?.komprimiert_size || 0), 0);
  const gespart = totalOrig && totalKomp ? Math.round((1 - totalKomp / totalOrig) * 100) : 0;

  return (
    <div className="tk-page">
      <div className="tk-header">
        {!portal && <button className="tk-back" onClick={() => navigate("/")}>← Home</button>}
        <h1>🗜️ Datei-Komprimierung</h1>
        <div className="tk-health">
          {health && (
            <>
              {(!allowed || allowed.bild) && <span className={`tk-pill ${health.image ? "ok" : "off"}`}>Bilder</span>}
              {(!allowed || allowed.video) && <span className={`tk-pill ${health.ffmpeg ? "ok" : "off"}`}>Videos {health.ffmpeg ? "" : "(ffmpeg fehlt)"}</span>}
              {(!allowed || allowed.pdf) && <span className={`tk-pill ${health.ghostscript ? "ok" : "off"}`}>PDFs {health.ghostscript ? "" : "(gs fehlt)"}</span>}
            </>
          )}
        </div>
      </div>

      <div ref={dropRef} className="tk-drop" onClick={() => inputRef.current?.click()}>
        <div className="tk-drop-icon">📥</div>
        <div className="tk-drop-text">
          Dateien hier ablegen oder klicken zum Auswählen
        </div>
        <div className="tk-drop-hint">
          {[
            (!allowed || allowed.bild) && "Bilder",
            (!allowed || allowed.video) && "Videos",
            (!allowed || allowed.pdf) && "PDFs",
          ].filter(Boolean).join(" · ")} · Mehrere gleichzeitig möglich
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={allowedExts.join(",")}
          style={{ display: "none" }}
          onChange={e => { addFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {files.length > 0 && (
        <>
          <div className="tk-actions">
            <button className="tk-btn primary" onClick={startCompression} disabled={busy || !files.some(f => f.status === "pending")}>
              {busy ? (uploadPct != null ? `Upload ${uploadPct} %…` : "Komprimiere…") : `Komprimieren (${files.filter(f => f.status === "pending").length})`}
            </button>
            {busy && uploadPct != null && (
              <button className="tk-btn ghost" onClick={() => abortRef.current?.abort()}>Upload abbrechen</button>
            )}
            <button className="tk-btn" onClick={downloadAll} disabled={!files.some(f => f.status === "done")}>
              ⬇ Alle herunterladen
            </button>
            {!portal && (
              <button className="tk-btn" onClick={uploadToSynology} disabled={synBusy || !files.some(f => f.syn && f.status === "done")}>
                {synBusy ? "Lädt…" : "📤 Originale auf Synology"}
              </button>
            )}
            <button className="tk-btn ghost" onClick={clearAll}>Liste leeren</button>
          </div>

          <div className="tk-summary">
            <div><strong>{files.length}</strong> Dateien · Original: <strong>{fmt(totalOrig)}</strong>
              {totalKomp > 0 && (<> · Komprimiert: <strong>{fmt(totalKomp)}</strong> · Ersparnis: <strong>{gespart}%</strong></>)}
            </div>
          </div>

          <div className="tk-list">
            <div className="tk-row tk-head">
              <div>Datei</div>
              <div>Original</div>
              <div>Komprimiert</div>
              <div>Ersparnis</div>
              <div>{portal ? "" : "Synology"}</div>
              <div>Aktion</div>
            </div>
            {files.map((f, i) => (
              <div key={i} className={`tk-row ${f.status}`}>
                <div className="tk-name">
                  {f.dropboxPath && <span className="tk-dbx-badge">☁️</span>}
                  <span title={f.file.name}>{f.file.name}</span>
                  {f.error && <div className="tk-err">{f.error}</div>}
                  {f.dbxError && <div className="tk-err">Dropbox: {f.dbxError}</div>}
                  {f.dbxDone && <div className="tk-ok">☁️ Ersetzt: {f.dbxPath}</div>}
                </div>
                <div>{fmt(f.result?.original_size || f.file.size)}</div>
                <div>{f.result?.komprimiert_size ? fmt(f.result.komprimiert_size)
                  : f.status === "uploading" ? `Upload ${uploadPct ?? 0} %`
                  : f.status === "processing" ? (f.progress != null && f.progress > 0 ? `${f.progress} %` : "wartet…")
                  : "—"}</div>
                <div>{f.result?.gespart_pct != null ? `${f.result.gespart_pct}%` : "—"}</div>
                <div>
                  {!portal && !f.dropboxPath && (
                    <input type="checkbox" checked={!!f.syn} onChange={() => toggleSyn(i)} disabled={f.status !== "done"} />
                  )}
                </div>
                <div className="tk-actions-cell">
                  {f.status === "done" && (
                    <button className="tk-btn small" onClick={() => downloadFile(f)}>⬇</button>
                  )}
                  {!portal && f.status === "done" && f.dropboxPath && !f.dbxDone && (
                    <button
                      className="tk-btn small"
                      onClick={() => replaceInDropbox(f, i)}
                      disabled={!!f.dbxBusy}
                      title={`Dropbox ersetzen: ${f.dropboxPath}`}
                    >
                      {f.dbxBusy ? "…" : "☁️ Dropbox"}
                    </button>
                  )}
                  <button className="tk-btn small ghost" onClick={() => removeItem(i)}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
